import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Tag, Table, Spin, message } from "antd";
import { EditOutlined, HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { SubmissionStatus } from "../../../common/utils";
import FileLink from "../FileLink";

const antIcon = <Spin size="large" />;

const SortableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999, background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {React.Children.map(props.children, (child) => {
        if (child.key === "dragHandle") {
          return React.cloneElement(child, {
            children: (
              <HolderOutlined
                style={{ cursor: "grab", color: "#999", fontSize: 16 }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const ApprovedMembers = ({ category }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const previousOrderRef = useRef([]);

  const history = useHistory();
  const [messageApi, contextHolder] = message.useMessage();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const fetchApplicationsByStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/team-application/category?category=${category}`
      );
      const data = await response.json();

      if (data.success) {
        setApplications(data.data);
        previousOrderRef.current = data.data.map((item) => item.id);
      }
    } catch (error) {
      console.log(error.response);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchApplicationsByStatus();
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setApplications((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    setHasOrderChanged(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    const items = applications.map((item, index) => ({
      id: item.id,
      displayOrder: index + 1,
    }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/team-application/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        }
      );
      const data = await response.json();

      if (data.success) {
        messageApi.success("Order saved successfully");
        previousOrderRef.current = applications.map((item) => item.id);
        setHasOrderChanged(false);
      } else {
        messageApi.error("Failed to save order");
        rollbackOrder();
      }
    } catch {
      messageApi.error("Failed to save order");
      rollbackOrder();
    }
    setSaving(false);
  };

  const rollbackOrder = () => {
    const prevIds = previousOrderRef.current;
    setApplications((current) => {
      const map = new Map(current.map((item) => [item.id, item]));
      return prevIds.map((id) => map.get(id)).filter(Boolean);
    });
    setHasOrderChanged(false);
  };

  const columns = [
    {
      title: "",
      dataIndex: "dragHandle",
      key: "dragHandle",
      width: 40,
      className: "drag-handle-cell",
    },
    {
      title: "#",
      key: "order",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Profile Image",
      dataIndex: "profile",
      key: "profile",
      render: (text) => <img src={text} alt="profile" width={50} height={50} style={{ borderRadius: "50%", objectFit: "cover" }} />,
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Email Address",
      dataIndex: "emailAddress",
      key: "emailAddress",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Resume",
      dataIndex: "resume",
      key: "resume",
      render: (text) => <FileLink url={text} label="Download Resume" />,
    },
    {
      title: "Cover Letter",
      dataIndex: "coverLetter",
      key: "coverLetter",
      render: (text, record) => (
        <FileLink
          url={text}
          label="Download Cover Letter"
          fileName={record.coverLetterName}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        let color = "geekblue";
        if (text === SubmissionStatus.PENDING) {
          color = "orange";
        } else if (text === SubmissionStatus.ACCEPTED) {
          color = "green";
        } else if (text === SubmissionStatus.REJECTED) {
          color = "red";
        }
        return (
          <Tag color={color} key={text}>
            {text.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => history.push(`/ambassadors/${record.id}`)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      {hasOrderChanged && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            type="primary"
            onClick={saveOrder}
            loading={saving}
            disabled={saving}
          >
            Save Order
          </Button>
          <Button onClick={rollbackOrder} disabled={saving}>
            Discard Changes
          </Button>
          <span style={{ color: "#999", fontSize: 13 }}>
            Drag rows to reorder, then click Save Order
          </span>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={applications.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            dataSource={applications}
            columns={columns}
            pagination={false}
            rowKey="id"
            loading={{
              spinning: loading,
              indicator: <Spin indicator={antIcon} />,
            }}
            locale={{ emptyText: "No applications found" }}
            components={{
              body: {
                row: SortableRow,
              },
            }}
            onRow={(record) => ({
              "data-row-key": record.id,
            })}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ApprovedMembers;
